/**
 * @prettier
 */

import React, { useEffect } from 'react';
import cx from 'classnames';
import { ButtonV2 } from '@zola/zola-ui';
import Loader from 'components/common/ui/Loader';
import UploadcareWidget from 'components/common/UploadcareWidget/UploadcareWidget';
import { isUploadcareEnabled } from 'components/common/UploadcareWidget/utils';
import Layout from '../Layout/Layout';
import PhotoUpload from '../PhotoUpload/PhotoUpload';
import { usePhotoUploadHandler } from '../util/hooks';
import FeatureFlags from '../../../../../util/featureFlags';
import styles from './styles.module.less';

const Crop = ({ template, form, updateForm, multiPreviewState, setMultiPreviewState }) => {
  const { photoSet } = form.customizations;

  const photo = photoSet[multiPreviewState.activePreview];

  useEffect(() => {
    if (!(photo && photo.valid)) {
      setMultiPreviewState({ ...multiPreviewState, cropEnabled: false });
    }
  }, [photo]);

  if (!template) {
    return <Loader />;
  }

  return (
    <div className={styles.cropBox}>
      <Layout.Header>Crop Your Photo</Layout.Header>
      <PhotoUpload.Crop
        activePreview={multiPreviewState.activePreview}
        form={form}
        page={template && template.pages[0]}
        updateForm={updateForm}
        isMulti
      />
      <ButtonV2
        disabled={!template}
        onClick={() => setMultiPreviewState({ ...multiPreviewState, cropEnabled: false })}
      >
        {template ? 'Save' : 'Loading...'}
      </ButtonV2>
    </div>
  );
};

const UploadButton = ({
  customizations,
  icon,
  templatePage,
  updateForm,
  multiPreviewState,
  setMultiPreviewState,
  isMobile,
  minDimensions,
  onSuccess,
  className = '',
  style,
}) => {
  const isUploadcare = isUploadcareEnabled && FeatureFlags.get('invitesMultiSampleFlowUploadcare');
  const elements = (templatePage && templatePage.elements) || [];

  const imageElement = elements.find(
    element => element.content_type === 'IMAGE' && element.customizable
  );

  const saveUploadcareImageToForm = uploadcareFile => {
    updateForm({
      customizations: {
        uploadcareFileSet: { [multiPreviewState.activePreview]: uploadcareFile },
      },
    });

    if (onSuccess) {
      onSuccess();
    }
  };

  const onSelect = usePhotoUploadHandler(
    updateForm,
    minDimensions,
    true,
    multiPreviewState.activePreview,
    null,
    null,
    () => {
      setMultiPreviewState({ ...multiPreviewState, cropEnabled: true });

      if (onSuccess) {
        onSuccess();
      }
    }
  );

  const renderButton = (onClick, disabled, label) => {
    return (
      <ButtonV2 className={styles.uploadButton} disabled={disabled} onClick={onClick} fullWidth>
        {icon}
        {label}
      </ButtonV2>
    );
  };

  let uploader;

  if (isUploadcare) {
    const cropRatio = imageElement ? `${imageElement.width}:${imageElement.height}` : null;
    const previousUploadcareFile =
      customizations.uploadcareFileSet[multiPreviewState.activePreview];

    uploader = (
      <UploadcareWidget
        cachedValue={previousUploadcareFile ? previousUploadcareFile.cdnUrl : null}
        cropRatio={cropRatio}
        onUploadcareFileReady={saveUploadcareImageToForm}
        renderButton={(inProgress, editable, onClick) =>
          renderButton(onClick, inProgress, 'Edit Photo')
        }
      />
    );
  } else {
    uploader = (
      <PhotoUpload.FileUploader
        onSelect={onSelect}
        renderButton={(onClick, inProgress) => renderButton(onClick, inProgress, 'Upload Photo')}
      />
    );
  }

  return (
    <div
      className={cx(className, styles.uploadButtonBox, { [styles.mobile]: isMobile })}
      style={style}
    >
      {uploader}
    </div>
  );
};

const CropButton = ({
  icon,
  multiPreviewState,
  setMultiPreviewState,
  isMobile,
  className = '',
  style,
}) => {
  const isUploadcare = isUploadcareEnabled && FeatureFlags.get('invitesMultiSampleFlowUploadcare');

  const onCropClick = () => {
    setMultiPreviewState({ ...multiPreviewState, cropEnabled: true });
  };

  const renderButton = (onButtonClick, disabled) => {
    return (
      <ButtonV2
        className={styles.uploadButton}
        disabled={disabled}
        onClick={onButtonClick}
        fullWidth
      >
        {icon}Crop Photo
      </ButtonV2>
    );
  };

  if (isUploadcare) {
    return null;
  }

  return (
    <div
      className={cx(className, styles.uploadButtonBox, { [styles.mobile]: isMobile })}
      style={style}
    >
      {renderButton(onCropClick)}
    </div>
  );
};

export default { Crop, UploadButton, CropButton };



// WEBPACK FOOTER //
// ./src/cards/components/common/SingleSample/MultiSample/PhotoUpload.jsx